/* eslint-disable eqeqeq */
import React from 'react';

import { Badge, Body, Button, formatMoney, FormattedMoney, Icon } from '@meterup/atto';
import { Quote } from '@meterup/connect-api';
import { QuoteState_State } from '@meterup/connect-api/src/connectProtos';
import {
  FormattedQuoteMonthlyAmount,
  FormattedSpeed,
  ProviderLogo,
  Tooltip,
} from '@meterup/connect-ui';
import cx from 'classnames';

import { formatBandwidth } from '../../../utils/formatBandwidth';
import { formatDataCap } from '../../../utils/formatDataCap';
import { formatSelectionPriority } from '../../../utils/formatSelectionPriority';
import ServiceabilityBadge from '../../Shared/ServiceabilityBadge/ServiceabilityBadge';

interface QuoteBlockProps {
  quote: Quote;
  selected: boolean;
  selectionPriority?: number;
  nextSelectionPriority?: number;
  preferred: boolean;
  disabledSelect: boolean;
  onClick: () => void;
  loading?: boolean;
}

function QuoteBlock({
  quote,
  selected,
  selectionPriority,
  nextSelectionPriority,
  preferred,
  disabledSelect,
  onClick,
  loading = false,
}: QuoteBlockProps) {
  const renderDesktopHeader = () => (
    <header className="flex flex-col">
      <div className="flex px-2 space-x-2">
        <span className="mr-1">
          <ServiceabilityBadge quote={quote} size="small" ends="pill" />
        </span>
        {preferred && (
          <>
            <span className="mr-1">
              <Badge size="small" variant="alternative" ends="pill">
                Priority Install
              </Badge>
            </span>
            <Badge size="small" variant="brand" ends="pill">
              Preferred
            </Badge>
          </>
        )}
      </div>
      <div className="flex items-center px-3">
        <div className="h-8 w-28 text-2xl flex items-center justify-between mt-2">
          {quote.provider && (
            <ProviderLogo
              name={quote.provider.name}
              path={quote.provider.path}
              url={quote.provider.logoUrl}
            />
          )}
        </div>
      </div>
    </header>
  );

  const renderMobileHeader = () => (
    <header className="flex justify-between items-center">
      <div className="h-8 w-22 text-lg flex items-center justify-between pr-2">
        {quote.provider && (
          <ProviderLogo
            name={quote.provider.name}
            path={quote.provider.path}
            url={quote.provider.logoUrl}
          />
        )}
      </div>
      <div className="flex items-center">
        {quote.availability && quote.availability !== 'unknown' && (
          <div className="pr-2">
            <ServiceabilityBadge quote={quote} size="x-small" />
          </div>
        )}
        {preferred && (
          <>
            <div className="pr-2">
              <Badge variant="alternative" size="x-small">
                Priority Install
              </Badge>
            </div>
            <div className="pr-2">
              <Badge variant="brand" size="x-small">
                Preferred
              </Badge>
            </div>
          </>
        )}
      </div>
    </header>
  );

  const renderDesktopContent = () => (
    <div className="flex w-full px-2 items-center gap-10">
      {/* Badges / Details column */}
      <div className="flex flex-col flex-1 text-right justify-end">
        <div className="text-xs text-gray-700 font-normal">{quote.connectionType?.name}</div>

        <div className="flex justify-end mt-2 space-x-1" style={{ minWidth: '200px' }}>
          <Tooltip
            title={
              quote.contractLengthMonths === 1
                ? 'Month-to-month contract'
                : `${quote.contractLengthMonths}-month contract`
            }
          >
            <Badge variant="neutral" size="small" icon="calendar" arrangement="leading-icon">
              {quote.contractLengthMonths}-Month
            </Badge>
          </Tooltip>

          <Tooltip
            title={
              quote.installFeeCents === 0
                ? 'Free install'
                : `${formatMoney({ amount: quote.installFeeCents / 100 })} install fee`
            }
          >
            <Badge variant="neutral" size="small" icon="wrench" arrangement="leading-icon">
              <FormattedMoney amount={Math.ceil(quote.installFeeCents / 100)} />
            </Badge>
          </Tooltip>

          {quote.dataCapGb > 0 && (
            <Tooltip title={`${formatDataCap(quote.dataCapGb)} monthly limit on data usage`}>
              <Badge variant="neutral" size="small" icon="speed-high" arrangement="leading-icon">
                {formatDataCap(quote.dataCapGb)}
              </Badge>
            </Tooltip>
          )}
        </div>

        <div className="flex justify-end mt-2 space-x-1">
          {quote.installationEstimateDays && (
            <Tooltip title={`${quote.installationEstimateDays} business days to install`}>
              <Badge variant="neutral" size="small" icon="clock" arrangement="leading-icon">
                {quote.installationEstimateDays} business days to install
              </Badge>
            </Tooltip>
          )}
        </div>
      </div>

      {/* Speeds column */}
      <div className="flex flex-col gap-2 flex-shrink-0" style={{ minWidth: '150px' }}>
        <div className="flex items-center whitespace-nowrap text-xs text-gray-700">
          <span className="text-gray-600 w-5 mr-2 inline-block">
            <Icon icon="arrow-down" />
          </span>
          <FormattedSpeed mbps={formatBandwidth(quote.downloadKbps)} /> down
        </div>
        <div className="flex items-center whitespace-nowrap text-xs text-gray-700">
          <span className="text-gray-600 w-5 mr-2 inline-block">
            <Icon icon="arrow-up" />
          </span>
          <FormattedSpeed mbps={formatBandwidth(quote.uploadKbps)} /> up
        </div>
      </div>
    </div>
  );

  const renderContentMobile = () => (
    <div>
      <div className="flex justify-between">
        <Body weight="bold">
          <div className="flex items-center">
            <span className="mr-1">
              <Icon icon="arrow-down" size={14} />
            </span>
            <span className="text-xs">
              <FormattedSpeed mbps={formatBandwidth(quote.downloadKbps)} />
            </span>
            <span className="text-xs text-gray-600">&nbsp;down</span>
          </div>
        </Body>
        <div className="text-xl flex items-center font-bold">
          <FormattedQuoteMonthlyAmount quote={quote} />
        </div>
      </div>

      <div className="flex justify-between mt-1">
        <Body weight="bold">
          <div className="flex items-center">
            <span className="mr-1">
              <Icon icon="arrow-up" size={14} />
            </span>
            <span className="text-xs">
              <FormattedSpeed mbps={formatBandwidth(quote.uploadKbps)} />
            </span>
            <span className="text-xs text-gray-600">&nbsp;up</span>
          </div>
        </Body>
        <div className="text-xs text-gray-600 font-normal">
          {quote.installFeeCents === 0 && 'Free install'}
          {quote.installFeeCents != 0 && (
            <span>
              <FormattedMoney amount={Math.ceil(quote.installFeeCents / 100)} /> Install fee
            </span>
          )}
        </div>
      </div>
    </div>
  );

  const ordered = quote.state?.state === QuoteState_State.complete;
  const orderInProgress = quote.state?.state === QuoteState_State.contract_requested;
  const disableButton = (!selected && disabledSelect) || ordered || orderInProgress;

  const renderButton = () => {
    let buttonText = selected ? 'Remove' : 'Select';
    if (selected && selectionPriority) {
      buttonText += ` ${formatSelectionPriority(selectionPriority)}`;
    } else if (!selected && nextSelectionPriority) {
      buttonText += ` ${formatSelectionPriority(nextSelectionPriority)}`;
    }
    return (
      <Button
        width="200px"
        variant={selected ? 'secondary' : 'primary'}
        size="large"
        disabled={disableButton}
        onClick={onClick}
        loading={loading}
      >
        {buttonText}
      </Button>
    );
  };

  const renderButtonWrapper = () => {
    if (!selected && disabledSelect) {
      return <Tooltip title="You can only select up to 3 quotes">{renderButton()}</Tooltip>;
    }
    if (ordered) {
      return <Tooltip title="Ordered">{renderButton()}</Tooltip>;
    }
    if (orderInProgress) {
      return <Tooltip title="Order in progress">{renderButton()}</Tooltip>;
    }
    return renderButton();
  };

  const renderFooter = () => (
    <footer className="flex flex-col px-4">
      <div className="text-lg flex font-bold justify-end">
        <FormattedQuoteMonthlyAmount quote={quote} />
      </div>

      <div className="flex mt-1 w-full justify-end">{renderButtonWrapper()}</div>
    </footer>
  );

  const renderFooterMobile = () => (
    <footer className="flex justify-between">
      <div>
        <div className="text-xs text-gray-700 font-normal">
          <span>{quote.connectionType && quote.connectionType.name}</span>
        </div>
        {quote.dataCapGb > 0 && (
          <div className="text-xs text-gray-600 font-normal mt-1">
            <span className="mr-1">
              <Icon icon="speed-high" size={12} />
            </span>
            <span>{formatDataCap(quote.dataCapGb)}/month data cap</span>
          </div>
        )}

        <div className="text-xs text-gray-600 font-normal mt-1">
          <span className="mr-1">
            <Icon icon="calendar" size={12} />
          </span>
          <span>
            {quote.contractLengthMonths == 1
              ? 'Month-to-month contract'
              : `${quote.contractLengthMonths}-month`}
          </span>
        </div>
        {quote.installationEstimateDays && (
          <div className="text-xs text-gray-600 font-normal mt-1">
            <span className="mr-1">
              <Icon icon="clock" size={12} />
            </span>
            <span>{quote.installationEstimateDays} business days to install</span>
          </div>
        )}
      </div>
      <div>{renderButtonWrapper()}</div>
    </footer>
  );

  const desktopQuoteBlockClasses = cx(
    // Padding
    'relative sm:px-4 py-3 hidden lg:flex',

    // Background
    {
      'dark:m-bg-blue-700 m-bg-blue-50 border-b border-gray-100 ': selected,
      'dark:m-bg-gray-700 m-bg-white border-b border-gray-100 hover:bg-brand-50 hover:border-brand-100':
        !selected,
    },

    // Layout
    'flex-rows items-center',
  );

  const mobileQuoteBlockClasses = cx(
    'lg:hidden',

    // Background
    {
      'dark:m-bg-blue-700 m-bg-gray-50 border border-gray-100 rounded-md mx-2 mt-4': selected,
      'dark:m-bg-gray-700 m-bg-white border border-gray-100 rounded-md mx-2 mt-4 hover:bg-brand-50':
        !selected,
    },
  );

  return (
    <div>
      <div className={mobileQuoteBlockClasses}>
        <div className="p-3 border-b border-gray-100">{renderMobileHeader()}</div>
        <div className="p-3 border-b border-gray-100">{renderContentMobile()}</div>
        <div className="p-3">{renderFooterMobile()}</div>
      </div>

      <div className={desktopQuoteBlockClasses}>
        <div className="lg:flex-1">{renderDesktopHeader()}</div>
        <div className="mt-5 lg:mt-0 lg:flex-3">{renderDesktopContent()}</div>
        <div className="mt-3 lg:mt-0 lg:flex-1">{renderFooter()}</div>
      </div>
    </div>
  );
}

export default QuoteBlock;
